import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT } from "@/data/products";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sub, setSub] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSub(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent", { description: "We'll be in touch soon." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Please try again.");
    } finally { setSub(false); }
  };

  return (
    <div className="section-y" data-testid="contact-page">
      <div className="container-x grid lg:grid-cols-2 gap-14">
        <div>
          <div className="overline">Contact</div>
          <h1 className="font-display text-5xl lg:text-6xl text-sage-800 mt-3 text-balance">Let's connect.</h1>
          <p className="mt-6 text-sage-800/70 max-w-md">Transform your mind, body, and quality of life with our team at Restore Wellness. We reply within one business day.</p>
          <ul className="mt-10 space-y-4 text-sage-800/80">
            <li className="flex gap-3"><MapPin className="w-5 h-5 shrink-0 mt-0.5 text-sage-700" /> {CONTACT.address}</li>
            <li><a href={`tel:${CONTACT.phone}`} className="flex gap-3 hover:text-sage-700"><Phone className="w-5 h-5 shrink-0 mt-0.5 text-sage-700" /> {CONTACT.phone}</a></li>
            <li><a href={`mailto:${CONTACT.email}`} className="flex gap-3 hover:text-sage-700"><Mail className="w-5 h-5 shrink-0 mt-0.5 text-sage-700" /> {CONTACT.email}</a></li>
            <li className="flex gap-3"><Clock className="w-5 h-5 shrink-0 mt-0.5 text-sage-700" /> {CONTACT.hours}</li>
          </ul>
          <div className="mt-8 rounded-3xl overflow-hidden aspect-[4/3] border border-sand-200/70">
            <iframe title="Location" className="w-full h-full" src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.address)}&output=embed`} loading="lazy" />
          </div>
        </div>
        <form onSubmit={submit} className="rounded-3xl bg-white border border-sand-200/70 p-8" data-testid="contact-form">
          <div className="font-display text-2xl text-sage-800">Send us a message</div>
          <div className="mt-6 space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} required data-testid="contact-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} required data-testid="contact-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="contact-phone" /></div>
            <div><Label>Message</Label><Textarea rows={5} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} required data-testid="contact-message" /></div>
          </div>
          <Button type="submit" disabled={sub} className="btn-pill h-12 w-full mt-6 bg-sage-700 hover:bg-sage-800 text-white" data-testid="contact-submit">{sub?"Sending…":"Send message"}</Button>
        </form>
      </div>
    </div>
  );
}
